import { action
       , computed
       , observable }             from 'mobx'

export declare type fetcher_key<VARS> = ( data: VARS ) => string ;
export declare type data_page_loader<T,VARS> = ( data: VARS, page: number ) => Promise< T[] > ;
export declare type num_pages_loader<T,VARS> = ( data: VARS ) => Promise< number > ;


export class PageFetcher<T>
{
  @observable public data: T[] = [] ;
  @observable public loading: boolean = false ;
  @observable public error: any = undefined ;
  when_data_is_ready: Promise< PageFetcher< T > > ;

  constructor ( loader: Promise< T[] > )
  {
    this.loading = true ;

    loader.then( this.on_set_data )
          .catch( this.on_error ) ;

    this.when_data_is_ready = loader.then( _ => this ) ;
  }

  @action public on_set_data = ( data: T[] ) =>
  {
    this.data    = data ;
    this.loading = false ;
  }

  @action public on_error = ( error: any ) =>
  {
    this.data    = [] ;
    this.error   = error ;
    this.loading = false ;
  }

   public async on_ready ( ) : Promise< PageFetcher<T> >
   {
     return this.when_data_is_ready ;
   }
}


type data_fetcher_loader<T> = ( page: number ) => Promise<T[]> ;

export class DataFetcher<T>
{
  @observable public data: Map< number, PageFetcher<T> > = new Map( ) ;
  @observable public num_pages: number ;
  @observable public cur_page:  number ; // in case someone wants to use it with a paginator
              protected get_page_data: data_fetcher_loader<T> ;


  constructor ( num_pages: Promise<number> | undefined, get_page_data: data_fetcher_loader<T> )
  {
    this.num_pages = 0 ;
    this.cur_page  = 0 ;
    this.get_page_data = get_page_data ;

    if ( num_pages )
         num_pages.then( total_pages => this.num_pages = total_pages ) ;
  }


  public reload_last_page ( )
  {
    const page = this.num_pages - 1 ;

    if ( page >= 0 )
         this.data.set( page, new PageFetcher( this.get_page_data( page ) ) ) ;
  }


  public reload_page ( page:number )
  {
    this.data.set( page, new PageFetcher( this.get_page_data( page ) ) ) ;
  }


  public data_for_page ( page: number )
  {
    if ( !this.data.has( page ) )
         this.data.set( page, new PageFetcher( this.get_page_data( page ) ) ) ;

    return this.data.get( page )! ;
  }

  @computed public get cur_page_data ( )
  {
    return this.data_for_page( this.cur_page ) ;
  }

  public reload_cur_page ( )
  {
    this.reload_page( this.cur_page ) ;
  }

  public reset ( )
  {
    this.data.clear( ) ;
  }
}


export class Fetcher<T,VARS>
{
   @observable public    cache        : Map< string, DataFetcher<T> > = new Map ( ) ;
               protected get_key      : fetcher_key<VARS> ;
               protected get_page_data: data_page_loader<T,VARS> ;
               protected get_num_pages?: num_pages_loader<T,VARS> ;

   constructor ( get_key: fetcher_key<VARS>
               , get_page_data: data_page_loader<T,VARS>
               , get_num_pages?: num_pages_loader<T,VARS> )
   {
     this.get_key       = get_key       ;
     this.get_page_data = get_page_data ;
     this.get_num_pages = get_num_pages ;
   }


   public data_for ( vars: VARS, page: number )
   {
     return this.data_fetcher_for( vars )
                .data_for_page( page ) ;
   }


   public num_pages ( vars: VARS )
   {
     return this.data_fetcher_for( vars ).num_pages ;
   }


   public reload_page ( vars: VARS, page: number )
   {
     const cache_key = this.get_key( vars ) ;

     if ( this.cache.has( cache_key ) )
          this.cache.get( cache_key )!.reload_page( page ) ;
   }


   public data_fetcher_for ( vars: VARS )
   {
     const cache_key = this.get_key( vars ) ;

     if ( !this.cache.has( cache_key ) )
          this.cache.set( cache_key
                         , new DataFetcher( this.get_num_pages? this.get_num_pages( vars ) : undefined
                                          , ( page: number ) => this.get_page_data( vars, page ) ) ) ;

     return this.cache.get( cache_key )! ;
   }

   public reset ( )
   {
     this.cache.forEach( data_fetcher => data_fetcher.reset( ) ) ;
     this.cache.clear( ) ;
   }
}
